import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PaymentStatusModel } from "../../../domain/model/Payment";
import { GetPaymentStatusUseCase } from "../../../domain/usecase/payment/status/UseCase";
import { State } from "../../../domain/model/ResponseState";
import { useSnackbar } from "notistack";
import { STORAGE_KEYS } from "../../../data/constant/Constants";
import SharedPreferences from "../../../core/utils/SharedPreferences";
import { CreateTransactionUseCase } from "../../../domain/usecase/transaction/create/CreateUseCase";
import {
  InvoiceTransaction,
  Transaction,
} from "../../../domain/model/Transaction";
import { RedeemDiscountCodeUseCase } from "../../../domain/usecase/discountCode/redeem/UseCase";
import { PlayerGetCurrentInfoUseCase } from "../../../domain/usecase/player/GetCurrentInfo/GetCurrentInfoUseCase";
import { UpdatePlayerUseCase } from "../../../domain/usecase/player/Update/UpdateUseCase";
import { LoginByUIDUseCase } from "../../../domain/usecase/auth/LoginByUID/UseCase";

export function PaymentStatusViewModel(
  isSuccessful: boolean,
  getPaymentStatusUseCase: GetPaymentStatusUseCase,
  createTransactionUseCase: CreateTransactionUseCase,
  redeemDiscountCodeUseCase: RedeemDiscountCodeUseCase,
  getCurrentInfoUseCase: PlayerGetCurrentInfoUseCase,
  updatePlayerUseCase: UpdatePlayerUseCase,
  loginByUIDUseCase: LoginByUIDUseCase
) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [paymentId, setPaymentId] = useState<string>();
  const searchParams = useSearchParams();

  const [numberOfPurchasedGames, setNumberOfPurchasedGames] =
    useState<number>();
  const [originalPriceInUSD, setOriginalPriceInUSD] = useState<number>();
  const [originalPriceInQAR, setOriginalPriceInQAR] = useState<number>();
  const [discount, setDiscount] = useState<number>();
  const [discountCodeId, setDiscountCodeId] = useState<string>();

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (paymentId) {
      handleApiCalls();
    }
  }, [paymentId]);

  async function handleApiCalls() {
    if (!paymentId) return;
    setLoading(true);
    const paymentStatusModel = await getPaymentStatus(paymentId);
    if (!isSuccessful) return;
    await applyPurchasedGamesToPlayer();
    if (!paymentStatusModel) {
      showSnackbar("paymentStatusModel undefined", "error");
      return;
    }
    await createTransaction(paymentStatusModel);
    if (discountCodeId) await redeemDiscountCode();
    SharedPreferences.setString(STORAGE_KEYS.LATEST_PAYMENT_ID, paymentId);
    setLoading(false);
  }

  async function init() {
    const searchParamsValue = searchParams[0];
    const numberOfPurchasedGames = searchParamsValue.get(
      "numberOfPurchasedGames"
    );
    const originalPriceInUSD = searchParamsValue.get("originalPriceInUSD");
    const originalPriceInQAR = searchParamsValue.get("originalPriceInQAR");
    const discount = searchParamsValue.get("discount");
    const discountId = searchParamsValue.get("discountId");
    const paymentId = searchParamsValue.get("paymentId");
    const userId = searchParamsValue.get("userId");

    if (!paymentId) return;
    if (!userId) return;
    if (!numberOfPurchasedGames && isSuccessful) return;
    if (!originalPriceInUSD && isSuccessful) return;
    if (!originalPriceInQAR && isSuccessful) return;
    if (discount == undefined && isSuccessful) return;

    setNumberOfPurchasedGames(Number(numberOfPurchasedGames));
    setOriginalPriceInUSD(Number(originalPriceInUSD));
    setOriginalPriceInQAR(Number(originalPriceInQAR));
    setDiscount(Number(discount));
    if (discountId) setDiscountCodeId(discountId);

    const latestPaymentId = SharedPreferences.getString(
      STORAGE_KEYS.LATEST_PAYMENT_ID
    );
    if (latestPaymentId == paymentId) return;
    await loginByUID(userId);
    setPaymentId(paymentId);
  }

  async function loginByUID(uid: string) {
    const response = await loginByUIDUseCase.invoke({ userId: uid });
    switch (response.responseState) {
      case State.Success:
        SharedPreferences.setString(
          STORAGE_KEYS.TOKEN,
          response.data?.accessToken!
        );
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function getPaymentStatus(
    paymentId: string
  ): Promise<PaymentStatusModel | undefined> {
    const response = await getPaymentStatusUseCase.invoke(paymentId);
    switch (response.responseState) {
      case State.Success:
        if (
          !response.data ||
          !response.data.status ||
          response.data.status?.InvoiceTransactions?.length == 0
        ) {
          return;
        }
        if (response.data.status.InvoiceTransactions![0].Error) {
          showSnackbar(response.data.status.InvoiceTransactions![0].Error);
          return;
        }

        return response.data?.status;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function createTransaction(paymentStatusModel: PaymentStatusModel) {
    const invoiceTransactions: Array<InvoiceTransaction> = [];
    paymentStatusModel.InvoiceTransactions?.map((element) => {
      const model = {
        transactionValue: element.TransactionValue,
        cardNumber: element.CardNumber,
        customerServiceCharge: element.CustomerServiceCharge,
        paidCurrencyValue: element.PaidCurrencyValue,
        paymentGateway: element.PaymentGateway,
        paymentId: element.PaymentId,
        referenceId: element.ReferenceId,
        trackId: element.TrackId,
        transactionId: element.TransactionId,
        transactionStatus: element.TransactionStatus,
      };
      invoiceTransactions.push(model);
    });

    const transactionCreateRequestModel: Transaction = {
      invoiceStatus: paymentStatusModel.InvoiceStatus,
      invoiceId: paymentStatusModel.InvoiceId?.toString(),
      invoiceReference: paymentStatusModel.InvoiceReference,
      invoiceDisplayValue: paymentStatusModel.InvoiceDisplayValue,
      invoiceTransactions: invoiceTransactions,
      discount: discount!,
      originalPriceInUSD: originalPriceInUSD!,
      originalPriceInQAR: originalPriceInQAR!,
      numberOfPurchasedGames: numberOfPurchasedGames!,
    };

    const response = await createTransactionUseCase.invoke(
      transactionCreateRequestModel
    );
    switch (response.responseState) {
      case State.Success:
        break;
      case State.Fail:
      case State.Error:
        await createTransaction(paymentStatusModel);
        break;
    }
  }

  async function applyPurchasedGamesToPlayer() {
    const response = await getCurrentInfoUseCase.invoke();
    switch (response.responseState) {
      case State.Success:
        {
          const player = response.data;
          if (!player || !numberOfPurchasedGames) return;

          player.gamesLeftToPlay! += numberOfPurchasedGames;
          player.usedFreeTrial = true;

          const updateResponse = await updatePlayerUseCase.invoke(player);

          switch (updateResponse.responseState) {
            case State.Fail:
            case State.Error:
              await applyPurchasedGamesToPlayer();
              break;
          }
        }
        break;
      case State.Fail:
      case State.Error:
        await applyPurchasedGamesToPlayer();
        break;
    }
  }

  async function redeemDiscountCode() {
    const response = await redeemDiscountCodeUseCase.invoke(
      discountCodeId!,
      numberOfPurchasedGames
    );
    switch (response.responseState) {
      case State.Success:
        break;
      case State.Fail:
      case State.Error:
        await redeemDiscountCode();
        break;
    }
  }

  function showSnackbar(
    message: string | undefined,
    variant: "error" | "success" = "error"
  ) {
    if (message) enqueueSnackbar(message, { variant: variant });
  }

  return { loading, discount, originalPriceInUSD, numberOfPurchasedGames };
}
